import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const MESH_HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_speedway-drops/artifacts/2toa5v6n_IMG_3884.jpeg",
  "https://customer-assets.emergentagent.com/job_speedway-drops/artifacts/akm9mp5j_IMG_3886.jpeg",
  "https://customer-assets.emergentagent.com/job_speedway-drops/artifacts/uoxreug5_IMG_3888.jpeg",
];

const MESH_LOOKBOOK_IMAGE = "https://customer-assets.emergentagent.com/job_speedway-drops/artifacts/6i9pdeal_IMG_3889.jpeg";

const MESH_REVIEWS = [
  {
    name: "Marcus T.",
    location: "Houston, TX",
    car: "C7 Corvette Z06 — Drag Build",
    finish: "Race Chrome",
    rating: 5,
    quote: "These wheels are the real deal. Fitment specialist caught a brake clearance issue I never would have spotted. Ran a 9.8 first time out — wheels never flinched. Worth every dollar.",
  },
  {
    name: "Devin R.",
    location: "Dallas, TX",
    car: "Audi R8 V10 — Drag Build",
    finish: "Signature Chrome",
    rating: 5,
    quote: "Custom 3-piece forged, true beadlock rear, and the fit on my R8 is flawless. The Lone Star team verified every spec before they built them. Quality is way above what I paid.",
  },
  {
    name: "Alex M.",
    location: "Austin, TX",
    car: "Lamborghini Huracán — 15\" Drag Setup",
    finish: "Signature Chrome",
    rating: 5,
    quote: "3.72 @ 196 mph and these wheels held up perfect. Beadlock rear is the move. Fitment process took an extra week but they got it right the first try.",
  },
  {
    name: "Chris H.",
    location: "San Antonio, TX",
    car: "Chevrolet Camaro Z/28",
    finish: "Signature Chrome",
    rating: 5,
    quote: "Polished lip, mesh face, sit perfectly flush. The shop owners actually care — texted me through the whole build with fitment options. This is how it should be done.",
  },
];

function Stars({ count = 5 }) {
  return (
    <div className="flex gap-1" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < count ? "text-adrenaline" : "text-zinc-700"}>★</span>
      ))}
    </div>
  );
}

const PHOTO_FIELDS = [
  { key: "side_profile", label: "Full Side Profile of Vehicle", required: true },
  { key: "front_wheel",  label: "Front Wheel Photo", required: true },
  { key: "rear_wheel",   label: "Rear Wheel Photo", required: true },
  { key: "brake_caliper",label: "Brake / Caliper Photo", required: true },
  { key: "suspension",   label: "Suspension Photo (if modified)", required: false },
];

const INTENDED_USE = ["Street", "Street/Strip", "Drag", "Show", "Daily"];
const SUSPENSION = ["Stock", "Lowered", "Coilovers", "Air Ride", "Mini Tub"];
const STANCE = ["Flush", "Aggressive", "Tucked", "Deep Lip", "Max Tire"];
const HARDWARE_COLORS = ["Polished", "Black", "Rose Gold PVD", "Chrome"];
const BRAKES = ["Factory", "Big Brake Kit"];

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const labelCls = "text-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400 mb-2 block";
const inputCls = "w-full bg-ink border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-adrenaline transition-colors";

function Field({ label, required, children }) {
  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-adrenaline ml-1">*</span>}</label>
      {children}
    </div>
  );
}

function StepHeader({ step, total, title }) {
  return (
    <div className="mb-10">
      <div className="text-mono text-[11px] uppercase tracking-[0.4em] text-adrenaline">Step {step} of {total}</div>
      <h2 className="text-display text-3xl md:text-5xl uppercase leading-tight mt-2">{title}</h2>
    </div>
  );
}

export default function MeshSeries() {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: finish + quantity
  const [finish, setFinish] = useState("signature_chrome");
  const [quantity, setQuantity] = useState(4);

  // Step 2: contact
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });

  // Step 3: fitment
  const [fitment, setFitment] = useState({
    year: "", make: "", model: "", trim: "", drivetrain: "",
    intended_use: "Street", suspension: "Stock", brake_setup: "Factory",
    bbk_brand: "", bbk_rotor_size: "",
    current_front_wheel: "", current_rear_wheel: "",
    current_front_tire: "", current_rear_tire: "",
    desired_stance: "Flush", desired_finish: "Signature Chrome",
    desired_hardware_color: "Polished", desired_beadlock_rear: false,
    bolt_pattern: "", center_bore: "", notes: "",
  });

  // Step 4: photos
  const [photos, setPhotos] = useState({});

  // Step 5: review
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    api.get("/mesh-series/config").then((r) => setConfig(r.data));
  }, []);

  useEffect(() => {
    // Auto-sync the "desired finish" to the chosen finish on step 1
    if (config && config.finishes[finish]) {
      setFitment((f) => ({ ...f, desired_finish: config.finishes[finish].label }));
    }
  }, [finish, config]);

  const finishMeta = useMemo(() => config?.finishes?.[finish], [config, finish]);
  const pricePerWheel = finishMeta?.price_per_wheel || 0;
  const amountDue = pricePerWheel * quantity;

  const onPhoto = async (key, file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Photo must be under 4MB");
      return;
    }
    const dataUrl = await fileToDataURL(file);
    setPhotos((p) => ({ ...p, [key]: dataUrl }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!customer.name.trim() || !customer.email.trim()) {
        toast.error("Name and email are required"); return false;
      }
    }
    if (step === 2) {
      const req = ["year", "make", "model"];
      for (const k of req) {
        if (!fitment[k]?.trim()) { toast.error(`Please fill in ${k}`); return false; }
      }
      if (fitment.brake_setup === "Big Brake Kit" && !fitment.bbk_brand?.trim()) {
        toast.error("Please provide your Big Brake Kit brand"); return false;
      }
    }
    if (step === 3) {
      const required = PHOTO_FIELDS.filter((p) => p.required).map((p) => p.key);
      for (const k of required) {
        if (!photos[k]) { toast.error(`Photo missing: ${PHOTO_FIELDS.find((p) => p.key === k).label}`); return false; }
      }
    }
    if (step === 4) {
      if (!acknowledged) { toast.error("Please confirm the fitment notice to proceed"); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => s + 1); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/mesh-series/order", {
        finish,
        quantity,
        customer_name: customer.name,
        customer_email: customer.email,
        customer_phone: customer.phone,
        fitment,
        photos,
        acknowledged: true,
        origin_url: window.location.origin,
      });
      // Fire GA conversion
      if (window.gtag) {
        window.gtag("event", "begin_checkout", {
          currency: "USD",
          value: amountDue,
          items: [{ item_id: `mesh_${finish}`, item_name: `Mesh ${finishMeta.label}`, price: pricePerWheel, quantity }],
        });
      }
      window.location.href = res.data.checkout_url;
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Order submission failed";
      toast.error(typeof msg === "string" ? msg : "Order submission failed");
      setSubmitting(false);
    }
  };

  if (!config) return <div className="bg-ink text-white min-h-screen pt-32 px-6">Loading…</div>;

  return (
    <div className="bg-ink text-white min-h-screen pb-24" data-testid="mesh-series-page">
      {/* HERO GALLERY */}
      <section className="pt-24 border-b border-white/5" data-testid="mesh-hero-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
          <div className="text-mono text-[11px] uppercase tracking-[0.4em] text-adrenaline">Lone Star Speed</div>
          <h1 className="text-display text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.95] mt-2">Signature Mesh Series</h1>
          <p className="text-zinc-400 mt-4 max-w-2xl text-lg">One design. Four finishes. 3-piece forged. Custom built to your vehicle and verified by a fitment specialist before production begins.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1 mt-10">
          {MESH_HERO_IMAGES.map((src) => (
            <div key={src} className="aspect-[4/3] overflow-hidden bg-black">
              <img src={src} alt="Lone Star Speed Signature Mesh Series build" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700" />
            </div>
          ))}
        </div>
        {/* Feature row */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { k: "6061-T6", v: "Forged Aluminum" },
              { k: "3-Piece", v: "Construction" },
              { k: "Beadlock", v: "Rear Options" },
              { k: "Made USA", v: "Built to Win" },
            ].map((s) => (
              <div key={s.k} className="border border-white/5 bg-carbon px-4 py-5">
                <div className="text-display text-2xl text-adrenaline">{s.k}</div>
                <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WIZARD */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-16">
        {/* Brand Lookbook */}
        <div className="border border-white/5 bg-black p-2 mb-12" data-testid="mesh-lookbook">
          <img
            src={MESH_LOOKBOOK_IMAGE}
            alt="Lone Star Speed Signature Mesh — One Design. Four Finishes."
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
        <div className="text-center mb-12">
          <div className="text-mono text-[11px] uppercase tracking-[0.4em] text-adrenaline">Build Your Set</div>
          <h2 className="text-display text-4xl md:text-5xl uppercase leading-none mt-2">Reserve Yours In 5 Steps</h2>
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto">Pick your finish, share your build, upload photos. Our fitment specialist takes it from there.</p>
        </div>
        <div className="section-divider mb-10" />

        {/* Step indicator */}
        <div className="flex flex-wrap gap-2 mb-10">
          {["Finish", "Contact", "Fitment", "Photos", "Review"].map((label, i) => (
            <div
              key={label}
              className={`text-mono text-[10px] uppercase tracking-[0.3em] px-3 py-2 border transition-colors ${
                i === step
                  ? "bg-adrenaline text-black border-adrenaline"
                  : i < step
                    ? "border-adrenaline/60 text-adrenaline"
                    : "border-zinc-700 text-zinc-500"
              }`}
              data-testid={`mesh-step-${i}`}
            >
              {i + 1}. {label}
            </div>
          ))}
        </div>

        {/* STEP 0 — Finish & quantity */}
        {step === 0 && (
          <div data-testid="mesh-step-finish">
            <StepHeader step={1} total={5} title="Select Your Finish" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
              {Object.entries(config.finishes).map(([key, meta]) => {
                const active = finish === key;
                return (
                  <button
                    key={key}
                    onClick={() => setFinish(key)}
                    data-testid={`mesh-finish-${key}`}
                    className={`text-left p-6 border transition-colors ${active ? "border-adrenaline bg-adrenaline/5" : "border-white/10 hover:border-white/30"}`}
                  >
                    <div className="text-display text-2xl uppercase">{meta.label}</div>
                    <div className="text-mono text-zinc-400 mt-1">${meta.price_per_wheel.toLocaleString()} / wheel · ${(meta.price_per_wheel * 4).toLocaleString()} / set of 4</div>
                  </button>
                );
              })}
            </div>

            <StepHeader step={1} total={5} title="How Many Wheels?" />
            <div className="grid grid-cols-4 gap-3 mb-6">
              {[1, 2, 3, 4].map((q) => (
                <button
                  key={q}
                  onClick={() => setQuantity(q)}
                  data-testid={`mesh-qty-${q}`}
                  className={`p-6 border transition-colors ${quantity === q ? "border-adrenaline bg-adrenaline/5" : "border-white/10 hover:border-white/30"}`}
                >
                  <div className="text-display text-4xl">{q}</div>
                  <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 mt-1">{q === 1 ? "Wheel" : "Wheels"}{q === 4 ? " · Full Set" : ""}</div>
                </button>
              ))}
            </div>
            <div className="bg-carbon border border-white/10 p-5 flex items-center justify-between flex-wrap gap-3 mb-10">
              <div>
                <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Order Total</div>
                <div className="text-display text-3xl text-adrenaline">${amountDue.toLocaleString()}</div>
                <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-1">{quantity} × ${pricePerWheel.toLocaleString()} per wheel · Paid in full at checkout</div>
              </div>
              <div className="text-zinc-500 text-xs max-w-xs text-right">Custom built and fitment-verified before production. Need replacements or odd-count? You're in the right place.</div>
            </div>
          </div>
        )}

        {/* STEP 1 — Contact */}
        {step === 1 && (
          <div data-testid="mesh-step-contact">
            <StepHeader step={2} total={5} title="Your Contact Info" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name" required>
                <input data-testid="mesh-customer-name" className={inputCls} value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
              </Field>
              <Field label="Email" required>
                <input data-testid="mesh-customer-email" type="email" className={inputCls} value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
              </Field>
              <Field label="Phone">
                <input data-testid="mesh-customer-phone" className={inputCls} value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
              </Field>
            </div>
          </div>
        )}

        {/* STEP 2 — Fitment */}
        {step === 2 && (
          <div data-testid="mesh-step-fitment">
            <StepHeader step={3} total={5} title="Vehicle & Fitment" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <Field label="Year" required><input data-testid="fit-year" className={inputCls} value={fitment.year} onChange={(e) => setFitment({ ...fitment, year: e.target.value })} /></Field>
              <Field label="Make" required><input data-testid="fit-make" className={inputCls} value={fitment.make} onChange={(e) => setFitment({ ...fitment, make: e.target.value })} /></Field>
              <Field label="Model" required><input data-testid="fit-model" className={inputCls} value={fitment.model} onChange={(e) => setFitment({ ...fitment, model: e.target.value })} /></Field>
              <Field label="Trim"><input className={inputCls} value={fitment.trim} onChange={(e) => setFitment({ ...fitment, trim: e.target.value })} /></Field>
              <Field label="Drivetrain"><input className={inputCls} placeholder="RWD / AWD / FWD" value={fitment.drivetrain} onChange={(e) => setFitment({ ...fitment, drivetrain: e.target.value })} /></Field>
              <Field label="Intended Use">
                <select className={inputCls} value={fitment.intended_use} onChange={(e) => setFitment({ ...fitment, intended_use: e.target.value })}>
                  {INTENDED_USE.map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Suspension">
                <select className={inputCls} value={fitment.suspension} onChange={(e) => setFitment({ ...fitment, suspension: e.target.value })}>
                  {SUSPENSION.map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Brake Setup">
                <select data-testid="fit-brake-setup" className={inputCls} value={fitment.brake_setup} onChange={(e) => setFitment({ ...fitment, brake_setup: e.target.value })}>
                  {BRAKES.map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Desired Stance">
                <select className={inputCls} value={fitment.desired_stance} onChange={(e) => setFitment({ ...fitment, desired_stance: e.target.value })}>
                  {STANCE.map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              {fitment.brake_setup === "Big Brake Kit" && (
                <>
                  <Field label="BBK Brand" required><input data-testid="fit-bbk-brand" className={inputCls} value={fitment.bbk_brand} onChange={(e) => setFitment({ ...fitment, bbk_brand: e.target.value })} /></Field>
                  <Field label="BBK Rotor Size"><input className={inputCls} placeholder='e.g. 15"' value={fitment.bbk_rotor_size} onChange={(e) => setFitment({ ...fitment, bbk_rotor_size: e.target.value })} /></Field>
                </>
              )}
              <Field label="Current Front Wheel Size"><input className={inputCls} placeholder='18x9.5 +35' value={fitment.current_front_wheel} onChange={(e) => setFitment({ ...fitment, current_front_wheel: e.target.value })} /></Field>
              <Field label="Current Rear Wheel Size"><input className={inputCls} placeholder='18x10 +25' value={fitment.current_rear_wheel} onChange={(e) => setFitment({ ...fitment, current_rear_wheel: e.target.value })} /></Field>
              <Field label="Current Front Tire"><input className={inputCls} placeholder='275/35R18' value={fitment.current_front_tire} onChange={(e) => setFitment({ ...fitment, current_front_tire: e.target.value })} /></Field>
              <Field label="Current Rear Tire"><input className={inputCls} placeholder='305/35R18' value={fitment.current_rear_tire} onChange={(e) => setFitment({ ...fitment, current_rear_tire: e.target.value })} /></Field>
              <Field label="Desired Hardware Color">
                <select className={inputCls} value={fitment.desired_hardware_color} onChange={(e) => setFitment({ ...fitment, desired_hardware_color: e.target.value })}>
                  {HARDWARE_COLORS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Beadlock Rear?">
                <select className={inputCls} value={fitment.desired_beadlock_rear ? "Yes" : "No"} onChange={(e) => setFitment({ ...fitment, desired_beadlock_rear: e.target.value === "Yes" })}>
                  <option>No</option><option>Yes</option>
                </select>
              </Field>
              <Field label="Bolt Pattern (if known)"><input className={inputCls} placeholder='5x4.75' value={fitment.bolt_pattern} onChange={(e) => setFitment({ ...fitment, bolt_pattern: e.target.value })} /></Field>
              <Field label="Center Bore (if known)"><input className={inputCls} placeholder='70.3mm' value={fitment.center_bore} onChange={(e) => setFitment({ ...fitment, center_bore: e.target.value })} /></Field>
            </div>
            <div className="mt-6">
              <Field label="Notes for Fitment Specialist">
                <textarea className={inputCls} rows={4} value={fitment.notes} onChange={(e) => setFitment({ ...fitment, notes: e.target.value })} placeholder="Anything we should know about your build, fender mods, ride height, future plans…" />
              </Field>
            </div>
          </div>
        )}

        {/* STEP 3 — Photos */}
        {step === 3 && (
          <div data-testid="mesh-step-photos">
            <StepHeader step={4} total={5} title="Vehicle Photos" />
            <p className="text-zinc-400 text-sm mb-6">Clear photos help our fitment specialist verify clearances. Max 4MB per photo.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {PHOTO_FIELDS.map((pf) => (
                <div key={pf.key} className="border border-white/10 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-mono text-[11px] uppercase tracking-[0.3em] text-zinc-300">{pf.label}{pf.required && <span className="text-adrenaline ml-1">*</span>}</span>
                    {photos[pf.key] && <span className="text-mono text-[10px] uppercase text-adrenaline">Uploaded</span>}
                  </div>
                  {photos[pf.key] ? (
                    <div className="relative">
                      <img src={photos[pf.key]} alt={pf.label} className="w-full h-48 object-cover" />
                      <button onClick={() => setPhotos((p) => { const n = { ...p }; delete n[pf.key]; return n; })} className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1">Remove</button>
                    </div>
                  ) : (
                    <label className="block cursor-pointer border-2 border-dashed border-white/15 hover:border-adrenaline transition-colors p-8 text-center">
                      <input data-testid={`mesh-photo-${pf.key}`} type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto(pf.key, e.target.files?.[0])} />
                      <div className="text-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">Click to upload</div>
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 4 — Review */}
        {step === 4 && (
          <div data-testid="mesh-step-review">
            <StepHeader step={5} total={5} title="Review & Confirm" />
            <div className="bg-carbon border border-white/10 p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Finish</div><div className="text-white">{finishMeta?.label}</div></div>
                <div><div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Quantity</div><div className="text-white">{quantity} {quantity === 1 ? "wheel" : "wheels"}{quantity === 4 ? " (Full Set)" : ""}</div></div>
                <div><div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Price Per Wheel</div><div className="text-white">${pricePerWheel.toLocaleString()}</div></div>
                <div><div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Total Due Today</div><div className="text-adrenaline text-2xl text-display">${amountDue.toLocaleString()}</div></div>
                <div><div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Vehicle</div><div className="text-white">{fitment.year} {fitment.make} {fitment.model} {fitment.trim}</div></div>
                <div><div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Customer</div><div className="text-white">{customer.name} — {customer.email}</div></div>
              </div>
            </div>

            <label className="flex gap-3 cursor-pointer bg-carbon border border-white/10 p-5 mb-6">
              <input data-testid="mesh-acknowledge" type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="mt-1 accent-adrenaline" />
              <span className="text-zinc-300 text-sm leading-relaxed">
                I understand Lone Star Speed wheels are custom built to order. My fitment will be reviewed before production. Final specs may be adjusted for proper clearance, stance, and tire compatibility. Payment is non-refundable once production begins.
              </span>
            </label>

            <button
              data-testid="mesh-submit"
              disabled={submitting || !acknowledged}
              onClick={submit}
              className="w-full bg-adrenaline text-black text-display text-xl uppercase py-5 tracking-wider hover:bg-adrenaline/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? "Processing…" : `Pay $${amountDue.toLocaleString()} & Submit Build`}
            </button>
            <p className="text-zinc-400 text-sm text-center mt-4">
              Every set is built specifically for your vehicle. We verify fitment before production so your wheels are engineered to fit, perform, and look right.
            </p>
          </div>
        )}

        {/* Navigation */}
        {step < 4 && (
          <div className="flex justify-between mt-12 pt-8 border-t border-white/10">
            <button
              onClick={back}
              disabled={step === 0}
              className="text-mono text-xs uppercase tracking-[0.3em] px-6 py-3 border border-white/20 hover:border-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="mesh-back"
            >
              ← Back
            </button>
            <button
              onClick={next}
              data-testid="mesh-next"
              className="text-mono text-xs uppercase tracking-[0.3em] px-8 py-3 bg-adrenaline text-black hover:bg-adrenaline/90 transition-colors"
            >
              Continue →
            </button>
          </div>
        )}
        {step === 4 && (
          <div className="flex justify-start mt-6">
            <button onClick={back} className="text-mono text-xs uppercase tracking-[0.3em] px-6 py-3 border border-white/20 hover:border-white transition-colors" data-testid="mesh-back">← Back</button>
          </div>
        )}
      </div>

      {/* REVIEWS */}
      <section className="border-t border-white/5 mt-24 pt-20 pb-8" data-testid="mesh-reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="text-mono text-[11px] uppercase tracking-[0.4em] text-adrenaline">Customer Builds</div>
              <h2 className="text-display text-4xl md:text-6xl uppercase leading-none mt-2">What Drivers Are Saying</h2>
            </div>
            <div className="flex items-center gap-4">
              <Stars count={5} />
              <div className="text-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">5.0 · Verified Builds</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {MESH_REVIEWS.map((r) => (
              <div key={r.name + r.car} className="bg-carbon border border-white/5 p-8 flex flex-col gap-5">
                <div className="flex justify-between items-start gap-4">
                  <Stars count={r.rating} />
                  <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-adrenaline">{r.finish}</div>
                </div>
                <p className="text-zinc-200 text-lg leading-relaxed">"{r.quote}"</p>
                <div className="border-t border-white/5 pt-4">
                  <div className="text-heading uppercase text-white tracking-wider">{r.name}</div>
                  <div className="text-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mt-1">{r.location} · {r.car}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">Reviews from verified Lone Star Speed customers</div>
          </div>
        </div>
      </section>
    </div>
  );
}
